/**
 * mergeGuestsSchema — SCRUM-256 (DS-018 §8/§10).
 *
 * Zod schema for `POST /organizations/:slug/guests/merge`. The single source of
 * truth for the merge request shape (CLAUDE.md rule 3 — never redefine a schema
 * locally in the API).
 *
 * Two-aggregate OCC (F10a): the request carries `expectedSurvivorVersion` AND
 * `expectedLoserVersion` (both required) in the BODY — not via `If-Match`, since
 * the operation spans two aggregates and a single ETag cannot express two
 * preconditions. A version mismatch on either aggregate is the merge-reserved
 * HTTP 409 (`MergeConflictError`), distinct from the single-resource 412 the
 * CRUD writes return for a stale `If-Match` (decision locked in SCRUM-255).
 *
 * Validation split (mirrors create/update, SCRUM-255):
 *   - This schema validates SHAPE only (→ HTTP 400): two uuids, two positive
 *     integer versions, and the structural `survivor !== loser` rule (a
 *     self-merge is a malformed request, not a domain state — caught at the
 *     boundary so the use case never has to special-case it).
 *   - All SEMANTIC merge preconditions (cross-org → 404 anti-enum; survivor/loser
 *     not Active → 409; OCC version mismatch → 409) are owned by
 *     `MergeGuestsUseCase` + RLS, NOT here.
 *
 * `.strict()` rejects unknown keys so a client cannot smuggle fields past the
 * boundary (mirrors create/update guest schemas).
 */
import { z } from 'zod';
export const mergeGuestsSchema = z
    .object({
    /** The guest that survives the merge (absorbs the loser; stays canonical). */
    survivorId: z.string().uuid(),
    /** The guest that is tombstoned and redirects to the survivor. */
    loserId: z.string().uuid(),
    /** OCC precondition for the survivor (its current `version`). */
    expectedSurvivorVersion: z.number().int().min(1),
    /** OCC precondition for the loser (its current `version`). */
    expectedLoserVersion: z.number().int().min(1),
})
    .strict()
    .refine((o) => o.survivorId !== o.loserId, {
    message: 'self_merge: survivorId and loserId must differ',
    path: ['loserId'],
});
//# sourceMappingURL=merge-guests.schema.js.map